import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import { API_BASE } from "config/api";

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  groupName: PropTypes.string,
  groupType: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  period: PropTypes.string,
  status: PropTypes.string,
  originalData: PropTypes.object,
});

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const GroupCell = ({ row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {row.original.groupName}
    </Typography>
    <Chip
      size="small"
      label={row.original.groupType}
      color={row.original.groupType === "Department" ? "info" : "warning"}
      sx={{ height: 18, fontSize: 10 }}
    />
  </Box>
);
GroupCell.propTypes = { row: PropTypes.shape({ original: rowPropTypes }).isRequired };

const MoneyCell = ({ value }) => (
  <Typography variant="button" fontWeight={600}>
    GHS {Number(value || 0).toFixed(2)}
  </Typography>
);
MoneyCell.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

const StatusCell = ({ value }) => (
  <Chip
    size="small"
    label={value}
    color={value === "paid" ? "success" : value === "overdue" ? "error" : "warning"}
    sx={{ textTransform: "capitalize" }}
  />
);
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete, canManage }) =>
  canManage ? (
    <Stack direction="row" spacing={1} justifyContent="center">
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<EditIcon fontSize="small" />}
        onClick={() => handleEdit(row.original)}
      >
        Edit
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon fontSize="small" />}
        onClick={() => handleDelete(row.original)}
      >
        Delete
      </Button>
    </Stack>
  ) : (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  canManage: PropTypes.bool.isRequired,
};

export default function useGroupDueData() {
  const { user, church } = useAuth();
  const canManage = ["admin", "super_admin", "pastor", "treasurer", "branch_admin"].includes(
    user?.role
  );

  const [groupDueData, setGroupDueData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Group", accessor: "groupName", align: "left", width: "22%", Cell: GroupCell },
      { Header: "Period", accessor: "period", align: "center", width: "13%", Cell: SerialCell },
      { Header: "Amount", accessor: "amount", align: "right", width: "13%", Cell: MoneyCell },
      { Header: "Status", accessor: "status", align: "center", width: "10%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            canManage={canManage}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
    summary: { total_due: 0, total_paid: 0, count: 0 },
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDue, setSelectedDue] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [ministries, setMinistries] = useState([]);

  const handleEdit = (due) => {
    setSelectedDue(due);
    setEditModalOpen(true);
  };

  const handleDelete = (due) => {
    setSelectedDue(due);
    setDeleteModalOpen(true);
  };

  const fetchGroupDues = useCallback(async () => {
    try {
      setGroupDueData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/group-dues`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const rows = result.data || [];

      setGroupDueData((prev) => ({
        ...prev,
        rows: rows.map((d, index) => ({
          id: d.id,
          serial: index + 1,
          departmentId: d.department_id,
          ministryId: d.ministry_id,
          groupName: d.department?.name || d.ministry?.name || "—",
          groupType: d.department_id ? "Department" : "Ministry",
          amount: d.amount,
          period: `${MONTHS[d.due_month - 1] || ""} ${d.due_year}`,
          dueMonth: d.due_month,
          dueYear: d.due_year,
          status: d.status,
          paidDate: d.paid_date,
          notes: d.notes,
          originalData: d,
        })),
        isLoading: false,
        error: null,
        summary: result.meta || { total_due: 0, total_paid: 0, count: 0 },
      }));
    } catch (error) {
      console.error("Fetch group dues failed:", error);
      setGroupDueData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [user, church]);

  const fetchGroupOptions = useCallback(async () => {
    if (!church?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };
      const [deptRes, minRes] = await Promise.all([
        fetch(`${API_BASE}/departments/church/${church.id}`, { headers }),
        fetch(`${API_BASE}/ministries/church/${church.id}`, { headers }),
      ]);
      const deptJson = deptRes.ok ? await deptRes.json() : [];
      const minJson = minRes.ok ? await minRes.json() : [];
      setDepartments(Array.isArray(deptJson) ? deptJson : deptJson?.data || []);
      setMinistries(Array.isArray(minJson) ? minJson : minJson?.data || []);
    } catch (err) {
      console.error("Failed to load department/ministry options:", err);
    }
  }, [church?.id]);

  const handleEditSubmit = async () => {
    try {
      if (!selectedDue.groupType) throw new Error("Choose a department or a ministry.");
      if (!selectedDue.groupRefId) throw new Error("Pick which department/ministry this is for.");
      if (!selectedDue.amount || Number(selectedDue.amount) <= 0)
        throw new Error("Enter an amount greater than zero.");

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedDue.id
        ? `${API_BASE}/group-dues/${selectedDue.id}`
        : `${API_BASE}/group-dues`;
      const method = selectedDue.id ? "PUT" : "POST";

      const body = {
        amount: selectedDue.amount,
        due_month: selectedDue.dueMonth,
        due_year: selectedDue.dueYear,
        status: selectedDue.status || "pending",
        paid_date: selectedDue.paidDate || null,
        notes: selectedDue.notes || "",
      };
      if (!selectedDue.id) {
        body.department_id = selectedDue.groupType === "Department" ? selectedDue.groupRefId : null;
        body.ministry_id = selectedDue.groupType === "Ministry" ? selectedDue.groupRefId : null;
      }

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({ title: "Success!", text: "Due saved", icon: "success" });
      fetchGroupDues();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Group due save failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/group-dues/${selectedDue.id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.message || `HTTP error! status: ${response.status}`);
      }
      Swal.fire({ title: "Success!", text: "Due deleted", icon: "success" });
      fetchGroupDues();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handleAddDue = () => {
    const now = new Date();
    setSelectedDue({
      id: null,
      groupType: "Department",
      groupRefId: "",
      amount: "",
      dueMonth: now.getMonth() + 1,
      dueYear: now.getFullYear(),
      status: "pending",
      paidDate: "",
      notes: "",
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => window.print();

  useEffect(() => {
    fetchGroupDues();
    fetchGroupOptions();
  }, [fetchGroupDues, fetchGroupOptions]);

  return {
    ...groupDueData,
    canManage,
    departments,
    ministries,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedDue,
    setSelectedDue,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddDue,
  };
}
